import api from "api"
import initialState from "../initialState"

export async function loadPrivileges(store, groupId) {
  const privileges = await api.get(`user/groups/${groupId}/privileges`)

  await store.update(state => {
    if (!state.privileges[groupId]) {
      state.privileges[groupId] = {}
    }
    privileges.forEach(
      privilege => (state.privileges[groupId][privilege] = true)
    )
  })
}

export async function loadSubscriptions(store, groupId) {
  const subscription = await api.get(`user/groups/${groupId}/subscription`)

  await store.update(state => {
    if (!state.subscriptions[groupId]) {
      state.subscriptions[groupId] = {}
    }
    state.subscriptions[groupId] = subscription
  })
}

export async function cancelSubscription(store, groupId) {
  const subscription = await (
    await api.fetch(`user/groups/${groupId}/subscription/cancel`, {}, "post", {
      plan_id: store.getState("subscriptions", groupId).plan_id,
      is_confirmed: true
    })
  ).json()

  if (!subscription.error) {
    await store.update(state => {
      if (!state.subscriptions[groupId]) {
        state.subscriptions[groupId] = {}
      }
      state.subscriptions[groupId] = subscription
    })
  }

  return subscription
}

export async function initiateSignin(store, email) {
  const response = await api.fetch("auth/signins", {}, "post", {
    app_id: store.meta.appId,
    type: "initiate",
    email
  })
  const data = await response.json()
  if (!data.error) {
    await store.update(state => {
      state.user.signinState = data
    })
  }
  return data
}

export async function resetSignIn(store, email) {
  await store.update(state => {
    state.user.signinState = null
  })
}

export async function changePassword(_, values) {
  await api.post("user/password", {}, values)
}

export async function signOut(store) {
  if (api.hasAuthToken()) {
    try {
      await api.post("user/session/signout?cookies", {}, {})
    } catch (error) {}
  }
  api.revokeAuth()
  await store.update(state => {
    return initialState
  })
}

export async function loadInvite(store, inviteCode) {
  const invite = await api.get(`auth/invites/${inviteCode}`)

  await store.update(state => {
    state.invite[inviteCode] = invite
  })
}

export async function signinWithPassword(store, { email, password, deviceId }) {
  const response = await api.fetch("auth/signins", {}, "post", {
    app_id: store.meta.appId,
    email,
    password,
    type: "password",
    ...(deviceId && { device_id: deviceId })
  })

  const data = await response.json()

  if (response.status === 200) {
    const { next_url, next_step } = data
    if (next_step.toLowerCase() === "finalize") {
      await store.actions.finalizeSignin(next_url)
    }
  }
  return data
}

export async function signinWithToken(store, { token, deviceId }) {
  const data = await api.post(
    "auth/signins",
    {},
    {
      type: "token",
      app_id: store.meta.appId,
      token,
      ...(deviceId && { device_id: deviceId })
    }
  )

  const { next_url, next_step } = data
  if (next_step.toLowerCase() === "finalize") {
    await store.actions.finalizeSignin(next_url)
  }
  return data
}

export async function validateMfa(store, { mfaCode, token, deviceId }) {
  try {
    const data = await api.post(
      `auth/signins/${token}/security-code`,
      {},
      {
        security_code: mfaCode,
        ...(deviceId && { device_id: deviceId })
      }
    )
    const { next_url, next_step } = data
    if (next_step.toLowerCase() === "finalize") {
      await store.actions.finalizeSignin(next_url)
    }
    return data
  } catch (error) {
    return { error: { fields: { mfaCode: "error" } } }
  }
}

export async function resendSecurityCode(store, token) {
  try {
    await api.put(`auth/signins/${token}/security-code`)
    return true
  } catch (error) {
    return false
  }
}

export async function finalizeSignin(store, finalizeUrl) {
  const result = await api.post(
    `${finalizeUrl}${
      !!process.env.REACT_APP_DISABLE_COOKIES ? "" : "?cookies"
    }  `
  )
  if (!!process.env.REACT_APP_DISABLE_COOKIES) {
    api.setBearerToken(result.session_token)
  } else {
    api.setBearerToken(result.csrf_token)
  }

  if (result.expires_at && result.renew_by) {
    api.setSessionData(result.expires_at, result.renew_by)
  }
  await store.actions.loadUser()
}

export async function renewToken() {
  const result = await api.post("user/session/renew")
  if (result.csrf_token) {
    api.setBearerToken(result.csrf_token)
  }
  if (result.expires_at && result.renew_by) {
    api.setSessionData(result.expires_at, result.renew_by)
  }
}

export async function requestRecoveryId(store, email, captcha_token) {
  const app_id = store.meta.appId
  const recovery = await api.post(
    `auth/recovery`,
    {},
    {
      app_id,
      email,
      captcha_token
    }
  )
  await store.update(state => {
    state.recovery = { email, ...recovery }
  })
}

export async function resetPasswordAndLogin(store, values) {
  const { recovery_code, new_password } = values
  const { id: recovery_id, email } = store.getState("recovery")
  const { next_url, next_step } = await api.post(
    `auth/reset-password`,
    {},
    {
      app_id: store.meta.appId,
      recovery_code,
      new_password,
      recovery_id,
      email
    }
  )

  if (next_step === "security-code") {
    return { next_url }
  }

  await store.actions.finalizeSignin(next_url)
}
